import { ImageResult } from '../types';
import logger from "@/utils/logger";
import { uploadGeneratedImageToCloud } from './backendService';

logger.log('[ImagePipeline] Image generation provider: DALL-E 3 (OpenAI) — Vertex AI Imagen disabled');

// State for throttling and deduplication
const activeGenerations = new Set<string>();
const generationTimestamps: number[] = [];
let activeConcurrency = 0;

// Configuration
const GENERATION_ENABLED = process.env.AI_IMAGE_GENERATION_ENABLED === 'true';
const MAX_CONCURRENT = parseInt(process.env.AI_IMAGE_GEN_MAX_CONCURRENT || '3', 10);
const RATE_LIMIT_PM = parseInt(process.env.AI_IMAGE_GEN_RATE_LIMIT_PER_MINUTE || '10', 10);
const MAX_TIMEOUT_MS = 30000;

function buildYogaPosePrompt(poseName: string, practiceDescription: string, vectorDescription?: string): string {
  const isWalking = poseName.toLowerCase().includes('walk') || poseName.toLowerCase().includes('hike');
  const isBreathing = poseName.toLowerCase().includes('breath') || poseName.toLowerCase().includes('pranayama') || poseName.toLowerCase().includes('anuloma') || poseName.toLowerCase().includes('kapalabhati') || poseName.toLowerCase().includes('bhedana');
  const isShavasana = poseName.toLowerCase().includes('shavasana') || poseName.toLowerCase().includes('savasana') || poseName.toLowerCase().includes('corpse pose') || poseName.toLowerCase().includes('resting') || poseName.toLowerCase().includes('viparita karani') || poseName.toLowerCase().includes('legs up');

  // Clean inputs to avoid string breaks
  const safeName = poseName.replace(/["\\]/g, '');
  const safeDescription = practiceDescription ? practiceDescription.replace(/"/g, '') : '';
  const safeVectorDesc = vectorDescription ? vectorDescription.replace(/"/g, '') : '';

  // Combine descriptions for maximum context
  const combinedDescription = safeVectorDesc ? `${safeDescription}. ${safeVectorDesc}` : safeDescription;

  const cleanPoseDescription = (text: string): string => {
    if (!text) return "";
    // Remove individual sentences containing dietary or non-physical prep keywords
    const sentences = text.split(/[.!?]+/).filter(sentence => {
      const s = sentence.toLowerCase();
      return !s.includes('chew') && 
             !s.includes('ginger') && 
             !s.includes('prior to') && 
             !s.includes('before practice') && 
             !s.includes('lime') && 
             !s.includes('salt') &&
             !s.includes('drink') &&
             !s.includes('eat');
    });
    return sentences.join('. ').trim();
  };

  const sanitizedDescription = cleanPoseDescription(combinedDescription);

  if (isWalking) {
    return `
    Professional wellness instruction photograph.
    A regular person walking calmly and mindfully on a nature trail.
    Action Description: ${combinedDescription || safeName}
    Setting: quiet natural outdoor setting, nature trail, soft sunlight.
    Style: natural, realistic, calm wellness photography, 
    warm soft lighting, everyday appearance, relaxed pace.
    NOT running, NOT sprinting, NOT dramatic pose — calm steady walk only.
    No text, no watermarks, no diagrams, single person, photorealistic.
  `.trim();
  }

  if (isBreathing) {
    return `
    A regular person sitting quietly and comfortably, 
    eyes gently closed, performing a breathing practice.
    Action Description: ${combinedDescription || safeName}
    Setting: clean minimalist studio, soft natural lighting, tranquil atmosphere.
    Style: peaceful, soft, realistic wellness photography.
    NOT exercising vigorously — seated stillness and calm only.
    No text, no watermarks, no medical diagrams, single person, photorealistic.
  `.trim();
  }

  if (isShavasana) {
    return `
    A regular person lying peacefully on a yoga mat 
    in complete rest.
    Action Description: ${sanitizedDescription || safeName}
    Setting: clean minimalist studio, soft natural lighting, white or light grey background.
    Style: serene, soft, realistic wellness photography, warm tones.
    NOT moving, NOT exercising — complete stillness and rest only.
    No text, no watermarks, no medical diagrams, single person, photorealistic.
    Crucial instruction: Depict ONLY the external physical action and visible, opaque body surface. 
    ABSOLUTELY NO internal anatomy, NO skeletal structures, NO bone highlights, NO X-ray effects, NO transparency.
  `.trim();
  }

  // Default template for physical asanas
  return `
  Professional wellness instruction photograph.
  A regular person in comfortable casual or athletic clothing 
  performing a physical wellness movement.
  CRITICAL ACTION TO DEPICT: ${sanitizedDescription || safeName}
  Setting: clean minimalist studio, soft natural lighting, white or light grey background.
  Camera: full body shot, natural perspective.
  Style: natural, calm, realistic wellness photography, 
  soft focus, warm tones, everyday person.
  The subject's pose must EXACTLY match the "CRITICAL ACTION TO DEPICT" description.
  Crucial instruction: Depict ONLY the external physical action and visible, opaque human skin and clothing surface. 
  ABSOLUTELY NO internal anatomy, NO skeletal structures, NO bone highlights, NO X-ray effects, NO transparency. 
  The person must appear as a normal, opaque human with visible skin and standard clothing only. Keep the pose realistic and anatomically correct from the outside.
  The skeletal system and bones must be COMPLETELY INVISIBLE. The lighting should emphasize the surface texture of clothing and skin, not the internal structure.
  No text overlays, no watermarks, no multiple people, photorealistic.
`.trim();
}

function extractMainIngredient(mealName: string): string | null {
  const ingredients = [
    'tofu', 'spinach', 'pear', 'cereal', 'kidney bean', 'lentil',
    'chickpea', 'salmon', 'chicken', 'lamb', 'beef', 'egg',
    'moong', 'dal', 'khichdi', 'quinoa', 'oat', 'yogurt',
    'avocado', 'broccoli', 'cauliflower', 'sweet potato', 'pumpkin'
  ];
  const nameLower = mealName.toLowerCase();
  return ingredients.find(i => nameLower.includes(i)) ?? null;
}

function isAyurvedicMeal(mealName: string): boolean {
  const nameLower = mealName.toLowerCase();

  // Primary Ayurvedic terms — direct match
  const ayurvedicTerms = [
    'khichdi', 'khichri', 'dal', 'daal', 'upma', 'idli', 'dosa',
    'tulsi', 'ajwan', 'ajwain', 'triphala', 'ashwagandha', 'amla',
    'rasam', 'sambhar', 'kitchari', 'trikatu', 'chana', 'moong',
    'masoor', 'toor', 'urad', 'methi', 'raita', 'kadhi', 'millet',
    'sabzi', 'chapati', 'paratha', 'turmeric oats', 'ginger oats',
    'spiced oats', 'ayurvedic oatmeal', 'oatmeal', 'oats', 'porridge', 'congee',
    'savory oats', 'savoury oats', 'overnight oats',
    'paneer', 'ghee', 'chutney', 'lassi', 'halwa', 'kheer', 'payasam', 'uttapam',
    'appam', 'pongal', 'kesari', 'kozhukattai',
    'poha', 'saag', 'palak', 'aloo', 'gobi', 'matar'
  ];

  const matchedTerm = ayurvedicTerms.find(term => nameLower.includes(term));
  if (matchedTerm) {
    console.log(`[ImagePipeline] Ayurvedic match: "${mealName}" matched term "${matchedTerm}"`);
    return true;
  }

  // Secondary compound check — generic dish format + Ayurvedic ingredient
  const ayurvedicIngredients = [
    'turmeric', 'ginger', 'cardamom', 'ashwagandha', 'tulsi',
    'neem', 'triphala', 'ajwain', 'cumin', 'fenugreek', 'amla'
  ];
  const genericFormats = ['broth', 'stew', 'soup', 'rice', 'oats', 'porridge', 'bowl'];

  const indianProteins = ['paneer', 'tofu', 'tempeh', 'chana', 'chickpea'];
  const cookingFormats = ['stir-fry', 'stir fry', 'sabzi', 'curry', 'masala'];

  const hasAyurvedicIngredient = ayurvedicIngredients.find(i => nameLower.includes(i));
  const hasGenericFormat = genericFormats.find(f => nameLower.includes(f));

  if (hasAyurvedicIngredient && hasGenericFormat) {
    console.log(`[ImagePipeline] Ayurvedic compound match: "${mealName}" (${hasGenericFormat} + ${hasAyurvedicIngredient})`);
    return true;
  }

  const hasIndianProtein = indianProteins.find(p => nameLower.includes(p));
  const hasCookingFormat = cookingFormats.find(f => nameLower.includes(f));

  if (hasIndianProtein && hasCookingFormat) {
    console.log(`[ImagePipeline] Ayurvedic compound match: "${mealName}" (${hasCookingFormat} + ${hasIndianProtein})`);
    return true;
  }

  // Catch healing broths and vegetable dishes that are clearly Ayurvedic in style
  if (/peppery|pepper/.test(nameLower) && /broth|soup/.test(nameLower)) {
    console.log(`[ImagePipeline] Ayurvedic style match: "${mealName}" (peppery broth)`);
    return true;
  }
  if (/warming|healing|cleansing|detox/.test(nameLower)) {
    console.log(`[ImagePipeline] Ayurvedic style match: "${mealName}" (healing term)`);
    return true;
  }

  return false;
}

function buildMealPrompt(
  mealName: string,
  benefitDescription: string,
  vectorDescription?: string
): string {
  const safeName = mealName ? mealName.replace(/"/g, '') : '';
  const safeBenefit = benefitDescription?.trim()
    ? benefitDescription.trim().replace(/"/g, '')
    : `A healthy Ayurvedic ${mealName} dish`;

  // Upma variant — must come before any porridge/oatmeal check
  if (/upma/.test(mealName.toLowerCase())) {
    console.log(`[ImagePipeline] Using UPMA prompt variant for "${mealName}"`);
    return `
      Professional Indian food photography of "${safeName}".
      Upma is a South Indian breakfast dish made from dry-roasted 
      semolina or coarse grain cooked with vegetables and spices.
      It has a fluffy, granular, crumbly texture — NOT smooth, 
      NOT liquid, NOT a flatbread.
      The dish is served mounded on a plate or in a bowl, 
      garnished with curry leaves, mustard seeds, and fresh 
      coriander. Colour is golden-yellow with visible vegetable 
      pieces mixed through.
      Setting: banana leaf or white plate, warm natural lighting.
      Style: authentic South Indian food photography, warm tones.
      NOT a flatbread, NOT a pancake, NOT a smooth porridge —
      a granular, fluffy, mounded grain dish.
      No text, no watermarks, photorealistic.
    `.trim();
  }

  // Dosa variant — must come before any flatbread or crepe check
  if (/dosa/.test(mealName.toLowerCase())) {
    console.log(`[ImagePipeline] Using DOSA prompt variant for "${mealName}"`);
    return `
      Professional South Indian food photography of "${safeName}".
      Dosa is a thin, crispy, golden-brown crepe made from 
      fermented grain and lentil batter.
      The dosa is shown folded or rolled on a plate, 
      thin and crispy with slightly browned edges.
      Served alongside a small bowl of sambar (thin lentil soup) 
      and coconut chutney on the side.
      Setting: banana leaf or white plate, warm natural lighting, 
      traditional South Indian presentation.
      Style: authentic, appetising South Indian food photography.
      NOT a thick pancake, NOT a flatbread like roti or paratha,
      NOT a yellow rice dish — a thin crispy crepe.
      No text, no watermarks, photorealistic.
    `.trim();
  }

  // Soup and Broth variant
  if (/soup|broth|stew/.test(mealName.toLowerCase())) {
    console.log(`[ImagePipeline] Using SOUP/BROTH prompt variant for "${mealName}"`);
    return `
    Professional food photography of "${safeName}".
    This is a warm, nourishing soup served in a ceramic bowl.
    ${safeBenefit}
    The image must show an appetising, inviting bowl of soup 
    with a clear or lightly coloured broth, visible ingredients 
    cut into neat pieces, garnished with fresh herbs on top.
    The presentation must look warm, comforting and appetising —
    ingredients should be in the background of the broth,
    not sticking out dramatically.
    NOT dark or dramatic — warm, golden, welcoming bowl of soup.
    Setting: wooden surface, warm natural side lighting, 
    overhead or 45-degree angle.
    Style: warm, comforting, realistic food photography, 
    golden tones, appetising.
    No text, no watermarks, photorealistic.
  `.trim();
  }

  // Steamed vegetable dish variant
  if (/steamed/.test(mealName.toLowerCase())) {
    console.log(`[ImagePipeline] Using STEAMED prompt variant for "${mealName}"`);
    return `
    Professional food photography of "${safeName}".
    This is a plate of steamed vegetables and tofu served warm.
    ${safeBenefit}
    The image must show steamed vegetables with visible steam, 
    plated simply on a white or ceramic plate.
    NOT fried, NOT raw, NOT a salad — gently steamed and warm.
    Setting: clean white plate, natural side lighting.
    Style: clean, simple, realistic healthy food photography.
    No text, no watermarks, photorealistic.
  `.trim();
  }

  // Oatmeal/porridge variant
  if (/oat|oatmeal|porridge/.test(mealName.toLowerCase())) {
    console.log(`[ImagePipeline] Using OATMEAL prompt variant for "${mealName}"`);
    return `
    Professional food photography of "${safeName}".
    This is a warm bowl of cooked oatmeal porridge.
    ${safeBenefit}
    The image must show a bowl of thick cooked oatmeal 
    topped with spices and garnishes.
    NOT a drink, NOT a smoothie, NOT a juice, NOT a glass — 
    this is a solid cooked oatmeal in a bowl with a spoon.
    Setting: wooden surface, warm morning light, overhead angle.
    Style: warm, cosy, realistic breakfast food photography.
    No text, no watermarks, photorealistic.
  `.trim();
  }

  // Dal variant
  if (/\bdal\b|\bdaal\b|lentil/.test(mealName.toLowerCase())) {
    console.log(`[ImagePipeline] Using DAL prompt variant for "${mealName}"`);
    return `
    Professional Indian food photography of "${safeName}".
    This is a traditional Indian lentil dal dish served in a bowl.
    ${safeBenefit}
    The image must show cooked lentils in a thick or soupy 
    consistency, orange or red coloured, served in a ceramic 
    or steel bowl, garnished with a tadka of cumin and 
    fresh coriander on top.
    NOT bread, NOT rice alone, NOT a salad — 
    this is a cooked lentil dal in a bowl.
    Setting: wooden surface, warm natural lighting, 
    overhead or 45-degree angle.
    Style: authentic, warm, realistic Indian food photography.
    No text, no watermarks, photorealistic.
  `.trim();
  }

  // Stir-fry variant
  if (/stir.?fry|stir fry/.test(mealName.toLowerCase())) {
    console.log(`[ImagePipeline] Using STIR-FRY prompt variant for "${mealName}"`);
    return `
    Professional food photography of "${safeName}".
    This is a cooked stir-fry dish served hot on a plate or in a wok.
    ${safeBenefit}
    The image must show sautéed vegetables and protein 
    with visible char and colour from high-heat cooking.
    NOT raw, NOT a salad, NOT a soup — 
    this is a hot stir-fried dish with sauce and garnish.
    Setting: dark wooden surface or cast iron pan, 
    warm dramatic side lighting.
    Style: vibrant, realistic, appetising food photography.
    No text, no watermarks, photorealistic.
  `.trim();
  }

  // Detect meal type from name to set correct visual context
  const isSalad = /salad|slaw|greens/i.test(mealName);
  const isSoup = /soup|stew|broth|dal|khichdi/i.test(mealName);
  const isBreakfast = /bowl|cereal|oatmeal|porridge|parfait|pudding/i.test(mealName);
  const isBeverage = /tonic|juice|smoothie|drink|milk|tea/i.test(mealName);
  const isSaute = /saute|sauté|stir-fry|pan-fried/i.test(mealName);

  const plating = isSalad
    ? 'served in a wide shallow bowl or plate, fresh and colourful'
    : isSoup
      ? 'served in a deep bowl with steam rising, warm and inviting'
      : isBreakfast
        ? 'served in a breakfast bowl, topped and garnished neatly'
        : isBeverage
          ? 'served in a glass or cup, garnished simply'
          : isSaute
            ? 'served in a shallow bowl or warm plate, freshly cooked'
            : 'plated neatly on a clean plate or bowl';

  const formatConstraint = isSoup
    ? 'Visually: Ensure it is a liquid-based dish served in a bowl, without solid baked goods on the side.'
    : isBreakfast
      ? 'Visually: Ensure it looks like lighter morning fare, without heavy dinner elements or baked loaves.'
      : isSaute
        ? 'Visually: Ensure it appears freshly cooked and mixed, not raw or submerged in deep liquid.'
        : '';

  const mainIngredient = extractMainIngredient(mealName);
  const ingredientAnchor = mainIngredient
    ? `The dish must visibly contain ${mainIngredient} as the main visible ingredient.`
    : '';

  let format = isSoup ? 'soup' : isBreakfast ? 'breakfast bowl' : isSaute ? 'sauté' : isSalad ? 'salad' : isBeverage ? 'beverage' : 'dish';
  const nameLower = mealName.toLowerCase();
  
  if (/upma/.test(nameLower)) format = 'Indian upma granular semolina grain dish bowl';
  if (/dosa/.test(nameLower)) format = 'South Indian dosa thin crispy crepe plate';
  if (/idli/.test(nameLower)) format = 'South Indian idli steamed rice cakes plate';
  if (/uttapam/.test(nameLower)) format = 'South Indian uttapam thick savory pancake plate';

  if (isAyurvedicMeal(mealName)) {
    return `
      Professional Indian food photography of "${safeName}".
      ${safeBenefit}
      This is a traditional Ayurvedic healing dish: ${safeName}.
      Dish format: ${format}.
      ${formatConstraint}
      Plating: served in a traditional Indian clay pot, steel bowl, 
      or white ceramic bowl on a wooden surface.
      Garnished simply with fresh herbs, a drizzle of ghee, 
      or a wedge of lemon as appropriate.
      Setting: warm natural lighting, rustic wooden or stone surface.
      Style: authentic, warm, realistic Indian food photography.
      The dish must clearly look like ${safeName} — 
      do not show any other dish.
      No text, no watermarks, photorealistic.
    `.trim();
  }

  return `
    Professional food photography of "${safeName}".
    This dish is: ${safeBenefit}
    The image must clearly show "${safeName}" — 
    the dish name is the absolute visual reference.
    Plating: ${plating}.
    Setting: clean wooden or white marble surface, 
    natural side lighting, 45-degree camera angle.
    Style: natural, appetising, realistic food photography, 
    vibrant natural colours.
    ${ingredientAnchor}
    ${formatConstraint}
    Clean composition focusing solely on the "${safeName}".
    No text, no watermarks, photorealistic.
  `.trim();
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function waitForCapacity(): Promise<boolean> {
  const startTime = Date.now();

  while (true) {
    if (Date.now() - startTime > MAX_TIMEOUT_MS) {
      return false; // Timed out waiting
    }

    // Clean up old timestamps for rate limiting
    const oneMinuteAgo = Date.now() - 60000;
    while (generationTimestamps.length > 0 && generationTimestamps[0] < oneMinuteAgo) {
      generationTimestamps.shift();
    }

    // Check capacity
    if (activeConcurrency < MAX_CONCURRENT && generationTimestamps.length < RATE_LIMIT_PM) {
      return true;
    }

    await delay(1000); // Check again in 1s
  }
}

export async function generateAndSaveImage(name: string, practiceDescription: string, type: 'pose' | 'meal', vectorDescription?: string): Promise<ImageResult> {
  const logPrefix = type === 'meal' ? `[ImagePipeline] MEAL "${name}" →` : `[ImagePipeline] POSE "${name}" →`;

  if (!GENERATION_ENABLED) {
    logger.log(`${logPrefix} GENERATION DISABLED via flag`);
    return { url: null, source: 'skipped', confidence: 0 };
  }

  const normalizedName = name.toLowerCase().trim();

  // Deduplication check
  let waitStart = Date.now();
  while (activeGenerations.has(normalizedName)) {
    if (Date.now() - waitStart > MAX_TIMEOUT_MS) {
      logger.log(`${logPrefix} TIMEOUT waiting for deduplication`);
      return { url: null, source: 'timeout', confidence: 0 };
    }
    await delay(500);
  }

  activeGenerations.add(normalizedName);

  try {
    const hasCapacity = await waitForCapacity();
    if (!hasCapacity) {
      logger.log(`${logPrefix} RATE LIMITED or timeout waiting for capacity`);
      return { url: null, source: 'timeout', confidence: 0 };
    }

    activeConcurrency++;
    generationTimestamps.push(Date.now());
    logger.log(`${logPrefix} DALL-E GENERATION TRIGGERED`);

    const prompt = type === 'pose'
      ? buildYogaPosePrompt(name, practiceDescription, vectorDescription)
      : buildMealPrompt(name, practiceDescription); // for meals, practiceDescription is vectorDescription 

    if (process.env.NODE_ENV === 'development') {
      logger.log(`\n[ImagePipeline] DALL-E prompt for ${name}:\n${prompt}\n`);
    }

    if (!prompt || prompt.trim().length < 20) {
      logger.error(`${logPrefix} DALL-E aborted — prompt is empty or too short: "${prompt}"`);
      return { url: null, source: 'error', confidence: 0 };
    }

    logger.log(`${logPrefix} DALL-E calling API — prompt length: ${prompt.length}`);

    let attempt = 0;
    let response;
    while (attempt < 2) {
      response = await fetch('/api/openai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: prompt
        })
      });

      if ((response.status === 429 || response.status >= 500) && attempt === 0) {
        logger.log(`${logPrefix} DALL-E ERROR ${response.status}. Retrying in 3 seconds...`);
        await delay(3000);
        attempt++;
        continue;
      }
      break;
    }

    if (!response || !response.ok) {
      const errText = await response?.text();
      logger.error(`${logPrefix} DALL-E FATAL ERROR ${response?.status}: ${errText}`);
      return { url: null, source: 'error', confidence: 0 };
    }

    const data = await response.json();
    const base64Data = data.data?.[0]?.b64_json;

    if (!base64Data) {
      logger.error(`${logPrefix} DALL-E RETURNED NO DATA`);
      return { url: null, source: 'error', confidence: 0 };
    }

    const filename = `${normalizedName.replace(/[^a-z0-9]/g, '-')}.jpg`;
    const categoryName = type === 'pose' ? 'yoga' : 'diet';

    // 1. Always attempt to push to Supabase Cloud Cache for persistent cross-session storage
    let cloudUrl: string | null = null;
    try {
      cloudUrl = await uploadGeneratedImageToCloud(filename, base64Data, categoryName as 'yoga' | 'diet');
      if (cloudUrl) logger.log(`[ImagePipeline] Persisted to Cloud: ${cloudUrl}`);
    } catch (e) {
      logger.error(`[ImagePipeline] Cloud upload failed:`, e);
    }

    // Attempt to download and save locally via our Vite dev plugin (dev only)
    if (process.env.NODE_ENV === 'development' || (import.meta as any).env?.DEV) {

      try {
        const saveResponse = await fetch('/api/save-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base64Data: base64Data,
            filename: filename,
            category: categoryName
          })
        });

        if (!saveResponse.ok) {
          const saveErr = await saveResponse.text();
          logger.error(`[ImagePipeline] generation error: failed to save locally: ${saveErr}`);
        } else {
          const saveData = await saveResponse.json();
          logger.log(`[ImagePipeline] generation saved locally for ${name} at ${saveData.localPath}`);
          return { url: saveData.localPath, source: 'ai-generated', confidence: 100 };
        }
      } catch (err: any) {
        logger.error(`[ImagePipeline] generation error: failed to fetch /api/save-image: ${err.message}`);
      }
    }

    // In production (or if saving fails in dev), return Cloud URL or fallback to base64
    const finalUrl = cloudUrl ? cloudUrl : `data:image/jpeg;base64,${base64Data}`;
    return { url: finalUrl, source: 'ai-generated', confidence: 100 };

  } catch (error: any) {
    logger.error(`[ImagePipeline] generation error for ${name}: ${error.message}`);
    return { url: null, source: 'error', confidence: 0 };
  } finally {
    activeConcurrency--;
    activeGenerations.delete(normalizedName);
  }
}
